// src/pages/Pagos.tsx

import { useState, useEffect } from 'react'
import { useTheme } from '../contexts/ThemeContext'
import { DollarSign, Search, Filter, Download, ChevronLeft, ChevronRight, Eye } from 'lucide-react'
import { adminPaymentService } from '../services/adminApiService'

export default function Pagos() {
  const { isDark } = useTheme()
  
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState({ status: 'all', method: 'all' })
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0
  })

  useEffect(() => {
    loadPayments()
  }, [pagination.currentPage, filters])

  const loadPayments = async () => {
    try {
      setLoading(true)
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== 'all')),
        search: searchQuery
      }

      const response = await adminPaymentService.getPayments(params)
      setPayments(response.payments || [])
      setPagination(prev => ({
        ...prev,
        totalItems: response.pagination?.totalItems || 0,
        totalPages: response.pagination?.totalPages || 0
      }))
    } catch (error) {
      console.error('Error loading payments:', error)
    } finally {
      setLoading(false)
    }
  }

  const cardClass = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
  const textClass = isDark ? 'text-white' : 'text-gray-900'
  const mutedTextClass = isDark ? 'text-gray-400' : 'text-gray-600'
  const inputClass = isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className={`text-3xl font-bold ${textClass} mb-2`}>
            Pagos y Transacciones
          </h1>
          <p className={`${mutedTextClass}`}>
            Historial de pagos ({pagination.totalItems} transacciones)
          </p>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por usuario o ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 rounded-lg border ${inputClass}`}
            />
          </div>

          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className={`px-4 py-3 rounded-lg border ${inputClass}`}
          >
            <option value="all">Todos los estados</option>
            <option value="completed">Completado</option>
            <option value="pending">Pendiente</option>
            <option value="failed">Fallido</option>
            <option value="refunded">Reembolsado</option>
          </select>
        </div>

        {/* Table */}
        <div className={`rounded-xl border ${cardClass} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`text-left py-3 px-4 font-medium ${textClass}`}>ID</th>
                  <th className={`text-left py-3 px-4 font-medium ${textClass}`}>Usuario</th>
                  <th className={`text-left py-3 px-4 font-medium ${textClass}`}>Monto</th>
                  <th className={`text-left py-3 px-4 font-medium ${textClass}`}>Estado</th>
                  <th className={`text-left py-3 px-4 font-medium ${textClass}`}>Fecha</th>
                  <th className={`text-left py-3 px-4 font-medium ${textClass}`}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id} className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <td className={`py-3 px-4 ${textClass}`}>#{payment.id}</td>
                    <td className={`py-3 px-4 ${mutedTextClass}`}>{payment.user_email}</td>
                    <td className={`py-3 px-4 ${textClass} font-medium`}>
                      ${payment.amount} {payment.currency}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                        payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {payment.status}
                      </span>
                    </td>
                    <td className={`py-3 px-4 ${mutedTextClass}`}>
                      {new Date(payment.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <button className="p-1 text-blue-500 hover:bg-blue-50 rounded">
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <span className={`text-sm ${mutedTextClass}`}>
                Mostrando {Math.min((pagination.currentPage - 1) * pagination.itemsPerPage + 1, pagination.totalItems)} - {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} de {pagination.totalItems}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                disabled={pagination.currentPage <= 1}
                className={`p-2 rounded ${pagination.currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className={`text-sm ${textClass}`}>
                Página {pagination.currentPage} de {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                disabled={pagination.currentPage >= pagination.totalPages}
                className={`p-2 rounded ${pagination.currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}